import { useCallback, useEffect, useRef, useState } from "react";

import { pickActiveSection } from "@/utils/activeSection";

import type { SectionPosition } from "@/utils/activeSection";

const measure = (ids: readonly string[]): SectionPosition[] =>
  ids.flatMap((id) => {
    const element = document.getElementById(id);
    if (!element) {
      return [];
    }
    return {
      id,
      top: element.getBoundingClientRect().top,
      // The CSS scroll-margin-top is the single source for the sticky-navbar
      // clearance, so a scrolled-to section always lands as "active".
      offset: parseFloat(getComputedStyle(element).scrollMarginTop) || 0,
    };
  });

const isScrolledToBottom = (): boolean =>
  window.innerHeight + window.scrollY >=
  document.documentElement.scrollHeight - 1;

interface ActiveSection {
  active: string | undefined;
  scrollTo: (id: string) => void;
}

/**
 * Tracks which of the given in-page sections (by element id, in page order)
 * the reader is currently in, and scrolls to one on request.
 *
 * After `scrollTo`, the requested section is reported active right away and
 * sections passed over by the smooth scroll are ignored until it is reached,
 * or until the reader takes over by scrolling themselves.
 */
const useActiveSection = (ids: readonly string[]): ActiveSection => {
  const [active, setActive] = useState<string | undefined>(ids[0]);
  const pendingRef = useRef<string | null>(null);

  useEffect(() => {
    let frame = 0;

    const update = () => {
      frame = 0;
      const next = pickActiveSection(measure(ids), isScrolledToBottom());
      if (pendingRef.current) {
        if (next !== pendingRef.current) {
          return;
        }
        pendingRef.current = null;
      }
      setActive(next);
    };
    const schedule = () => {
      if (!frame) {
        frame = requestAnimationFrame(update);
      }
    };
    const release = () => {
      pendingRef.current = null;
    };

    update();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    window.addEventListener("wheel", release, { passive: true });
    window.addEventListener("touchmove", release, { passive: true });

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      window.removeEventListener("wheel", release);
      window.removeEventListener("touchmove", release);
    };
  }, [ids]);

  const scrollTo = useCallback((id: string) => {
    pendingRef.current = id;
    setActive(id);
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, []);

  return { active, scrollTo };
};

export default useActiveSection;
